import { Spin, Typography } from "antd"
import React, { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useIdempotencyKey } from "../../../hooks/use-idempotency-key.ts"
import type { RootState } from "../../../store.ts"
import { LEAD_SOURCE_NONE } from "../../Leads/constants/lead-source-none.ts"
import { fetchProjectsFilterOptions } from "../../Projects/redux/projects.thunks.ts"
import {
  projectsViewPath,
  useProjectsView
} from "../../Projects/helpers/projects-view-preference.ts"
import { getProjectNavigatePath } from "../helpers/get-project-navigate-path.tsx"
import { projectTypeCategoryUpdates } from "../helpers/project-type-category-updates.ts"
import {
  createProject,
  updateProjectTypeToProject
} from "../redux/project.thunks.ts"
import { ProjectForm } from "./components/ProjectForm.tsx"
import { type ProjectFormData } from "./components/project-form-data.ts"

export const ProjectAddPage = ({
  user,
  fetching,
  filterOptions,
  saving,
  fetchFilterOptions,
  createProject,
  updateProjectType
}: ConnectedProps<typeof connector>) => {
  const navigate = useNavigate()
  const [projectsView] = useProjectsView()
  const { key: idempotencyKey, reset: resetIdempotencyKey } =
    useIdempotencyKey()

  const options = useMemo(
    () => ({
      state: filterOptions?.state,
      sales_manager: filterOptions?.sales_manager,
      source: filterOptions?.source,
      contact_method: filterOptions?.contact_method,
      project_manager: filterOptions?.project_manager,
      subcontractor: filterOptions?.subcontractor?.map((s) => ({
        label: s?.label,
        value: s?.value?.id
      }))
    }),
    [
      filterOptions?.project_manager,
      filterOptions?.state,
      filterOptions?.sales_manager,
      filterOptions?.source,
      filterOptions?.contact_method,
      filterOptions?.subcontractor
    ]
  )

  const handleCreateProject = async ({
    project_type_categories,
    ...data
  }: ProjectFormData) => {
    try {
      const project = await createProject({
        ...data,
        idempotency_key: idempotencyKey
      }).unwrap()

      // The attempt is over. Without this the page, which stays mounted when a
      // follow-up call below fails, would replay this project for a resubmit
      // the user had meanwhile edited.
      resetIdempotencyKey()

      for (const update of projectTypeCategoryUpdates(
        data?.project_type,
        project_type_categories
      )) {
        await updateProjectType({
          projectId: project.id,
          data: update
        }).unwrap()
      }

      navigate(
        getProjectNavigatePath(
          project,
          user?.id,
          user?.role?.id,
          projectsViewPath(projectsView)
        )
      )
    } catch (e) {
      console.error("Create project error:", e)
    }
  }

  useEffect(() => {
    generateDocumentTitle("Project Add")
    fetchFilterOptions()
  }, [fetchFilterOptions])

  return (
    <div className={"grid"}>
      {fetching && <Spin fullscreen spinning />}

      <FixedContainer>
        <Typography.Title level={2} className={"text-start"}>
          Create New Project
        </Typography.Title>

        <ProjectForm
          saving={saving}
          options={options}
          initialValues={{ source: LEAD_SOURCE_NONE }}
          onSubmit={handleCreateProject}
        />
      </FixedContainer>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth?.userInfo,
  fetching: state.projects.fetchingFilterOptions,
  filterOptions: state.projects.filterOptions,
  saving: state.project.saveFetching
})

const mapDispatchToProps = {
  createProject,
  updateProjectType: updateProjectTypeToProject,
  fetchFilterOptions: fetchProjectsFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectAddPage)
